import Papa from 'papaparse'

const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes

// Data normalizer - maps CSV columns to consistent internal property names
// This makes the codebase resilient to CSV structure changes
const COLUMN_MAPPING = {
  // Core facility properties
  name: ['name', 'facility_name', 'institution_name'],
  id: ['facility_id', 'id'],
  level: ['facility_level', 'level'],
  ownership: ['ownership'],
  officer_in_charge: ['officer_in_charge', 'oic'],
  phone: ['phone', 'oic_phone'],
  email: ['email', 'oic_email'],

  // Education-specific properties
  school_name: ['school_name', 'institution_name'],
  institution_type: ['institution_type'],
  total_learners: ['total_learners'],
  boys_count: ['boys_count'],
  girls_count: ['girls_count'],
  disabled_learners: ['disabled_learners'],
  total_teachers: ['total_teachers'],
  male_teachers: ['male_teachers'],
  female_teachers: ['female_teachers'],
  govt_payroll_teachers: ['govt_payroll_teachers'],
  diploma_teachers: ['diploma_teachers'],
  degree_teachers: ['degree_teachers'],
  certificate_teachers: ['certificate_teachers'],
  classroom_blocks: ['classroom_blocks'],
  permanent_classrooms: ['permanent_classrooms'],
  semi_permanent_classrooms: ['semi_permanent_classrooms'],
  temporary_classrooms: ['temporary_classrooms'],
  total_toilets: ['total_toilets'],
  separate_gender_toilets: ['separate_gender_toilets'],
  boys_toilets: ['boys_toilets'],
  girls_toilets: ['girls_toilets'],
  teacher_toilets_available: ['teacher_toilets_available'],
  teacher_toilets_count: ['teacher_toilets_count'],
  disability_accessible_toilets: ['disability_accessible_toilets'],
  water_available: ['water_available'],
  handwashing_available: ['handwashing_available'],
  electricity_available: ['electricity_available'],
  ict_lab_available: ['ict_lab_available'],
  pupil_classroom_ratio: ['pupil_classroom_ratio'],
  pupil_teacher_ratio: ['pupil_teacher_ratio'],
  pupil_textbook_ratio: ['pupil_textbook_ratio'],
  pupil_toilet_ratio: ['pupil_toilet_ratio'],

  // Location properties
  district: ['district'],
  subcounty: ['subcounty'],
  parish: ['parish'],
  village: ['village'],
  location_code: ['location_code'],
  latitude: ['latitude', 'lat'],
  longitude: ['longitude', 'lng', 'long'],

  // Service properties (normalized to consistent naming)
  immunization_services: ['immunization_services', 'has_immunization'],
  maternal_and_child_health_services: [
    'maternal_and_child_health_services',
    'has_maternal_health',
  ],
  hivaids_and_tb_care: ['hivaids_and_tb_care', 'has_hiv_tb_care'],
  basic_diagnostic_services: ['basic_diagnostic_services', 'has_diagnostics'],
  counselling: ['counselling', 'has_counselling'],
  health_education: ['health_education', 'has_health_education'],
  family_planning_services: ['family_planning_services', 'has_family_planning'],
  advanced_surgical_procedures: ['advanced_surgical_procedures', 'has_surgery'],
  outpatient_services: ['outpatient_services', 'has_outpatient'],

  // Infrastructure properties
  functional_backup_power: ['functional_backup_power', 'has_backup_power'],
  laboratory: ['laboratory', 'has_laboratory'],
  treatment_room: ['treatment_room', 'has_treatment_room'],
  consulting_room: ['consulting_room', 'has_consulting_room'],
  staff_accommodation: ['staff_accommodation', 'has_staff_accommodation'],
  reception: ['reception', 'has_reception'],
  waste_disposal_incinerator: [
    'waste_disposal_incinerator',
    'has_waste_disposal',
  ],
  gate: ['gate', 'has_gate'],
  delivery_room: ['delivery_room', 'has_delivery_room'],
  inpatient_ward: ['inpatient_ward', 'has_inpatient_ward'],

  // Staff properties (normalized names)
  laboratory_assistants: ['laboratory_assistants', 'staff_lab_assistant'],
  laboratory_technicians: [
    'laboratory_technicians',
    'laboratory_technician',
    'staff_lab_technician',
  ],
  medical_officers: ['medical_officers', 'staff_medical_officers'],
  nursing_assistants: ['nursing_assistants'],
  clinical_officers: ['clinical_officers', 'staff_clinical_officer'],
  enrolled_midwives: ['enrolled_midwives', 'staff_midwives'],
  nursing_officers: ['nursing_officers', 'staff_nursing_officers'],
  vhts_chws: ['vhts_chws', 'staff_vht_chw'],
  comprehensive_enrolled_nurse: [
    'comprehensive_enrolled_nurse',
    'staff_comprehensive_nurse',
  ],
  enrolled_nurses: ['enrolled_nurses', 'staff_enrolled_nurses'],
  health_information_assistants: [
    'health_information_assistants',
    'staff_health_info',
  ],

  // Supplies properties
  digital_respiratory_timers: [
    'digital_respiratory_timers',
    'has_respiratory_timers',
  ],
  oral_rehydration_salts: [
    'oral_rehydration_salts',
    'oral_rehydration_salts_ors_sachets_for_management_of_diarrhoea',
    'has_ors_sachets',
  ],
  zinc_tablets: [
    'zinc_tablets',
    'zinc_tablets_for_treating_diarrhoea',
    'has_zinc_tablets',
  ],
  rapid_diagnostic_test_kits_for_malaria: [
    'rapid_diagnostic_test_kits_for_malaria',
    'rapid_diagnostic_test_rdt_kits_for_malaria',
    'has_rdt_kits',
  ],
  act_tablets_for_treatment_of_malaria: [
    'act_tablets_for_treatment_of_malaria',
    'has_act_tablets',
  ],
  amoxicillin_tablets_for_treatment_of_pneumonia: [
    'amoxicillin_tablets_for_treatment_of_pneumonia',
    'has_amoxicillin',
  ],
  digital_clinical_thermometers_for_measuring_fever: [
    'digital_clinical_thermometers_for_measuring_fever',
    'has_thermometers',
  ],

  // Water & sanitation properties
  toilet_for_females: ['toilet_for_females', 'has_female_toilet'],
  toilet_for_people_with_reduced_mobility: [
    'toilet_for_people_with_reduced_mobility',
    'has_accessible_toilet',
  ],
  toilet_for_staff: ['toilet_for_staff', 'has_staff_toilet'],
  hand_washing_facilities: ['hand_washing_facilities', 'has_handwashing'],
}

/**
 * Normalizes facility data from CSV to consistent internal structure
 * @param {Object} rawData - Raw CSV row data
 * @returns {Object} - Normalized facility object
 */
function normalizeFacilityData(rawData) {
  const normalized = {}

  // Apply column mapping
  Object.entries(COLUMN_MAPPING).forEach(([normalizedKey, possibleColumns]) => {
    const foundColumn = possibleColumns.find(
      col => rawData[col] !== undefined && rawData[col] !== ''
    )
    if (foundColumn !== undefined) {
      normalized[normalizedKey] = rawData[foundColumn]
    }
  })

  // Copy any unmapped properties directly (like coordinates, thematic_area, etc.)
  Object.keys(rawData).forEach(key => {
    if (!Object.values(COLUMN_MAPPING).flat().includes(key)) {
      normalized[key] = rawData[key]
    }
  })

  return normalized
}

// Check if data is in localStorage and not stale
const getCachedData = key => {
  try {
    const cached = localStorage.getItem(key)
    if (!cached) return null

    const { data, timestamp } = JSON.parse(cached)
    const isStale = Date.now() - timestamp > CACHE_DURATION

    if (isStale) {
      localStorage.removeItem(key)
      return null
    }

    return data
  } catch (error) {
    window.console.warn(`Failed to get cached data for ${key}:`, error)
    return null
  }
}

// Store data in localStorage with timestamp
const setCachedData = (key, data) => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(cacheObject))
  } catch (error) {
    window.console.warn(`Failed to cache data for ${key}:`, error)
  }
}

// Generic JSON loader with caching
const loadJson = async (url, cacheKey) => {
  const cached = getCachedData(cacheKey)
  if (cached) {
    return cached
  }

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.statusText}`)
  }

  const data = await response.json()
  setCachedData(cacheKey, data)
  return data
}

// Generic CSV loader with caching
const loadCsv = async (url, cacheKey) => {
  const cached = getCachedData(cacheKey)
  if (cached) {
    return cached
  }

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.statusText}`)
  }

  const csvText = await response.text()

  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: header => {
        // Clean header names
        return header.trim().toLowerCase().replace(/\s+/g, '_')
      },
      transform: (value, field) => {
        // Convert numeric fields to numbers
        const numericFields = [
          'latitude',
          'longitude',
          // Health numeric fields
          'laboratory_assistants',
          'laboratory_technicians',
          'medical_officers',
          'nursing_assistants',
          'clinical_officers',
          'enrolled_midwives',
          'nursing_officers',
          'vhts_chws',
          'comprehensive_enrolled_nurse',
          'enrolled_nurses',
          'health_information_assistants',
          // Education numeric fields
          'total_learners',
          'boys_count',
          'girls_count',
          'disabled_learners',
          'total_teachers',
          'male_teachers',
          'female_teachers',
          'govt_payroll_teachers',
          'diploma_teachers',
          'degree_teachers',
          'certificate_teachers',
          'classroom_blocks',
          'permanent_classrooms',
          'semi_permanent_classrooms',
          'temporary_classrooms',
          'total_toilets',
          'boys_toilets',
          'girls_toilets',
          'teacher_toilets_count',
          'pupil_classroom_ratio',
          'pupil_teacher_ratio',
          'pupil_textbook_ratio',
          'pupil_toilet_ratio',
        ]

        if (numericFields.includes(field)) {
          const num = parseFloat(value)
          return isNaN(num) ? 0 : num
        }

        return value?.trim() || ''
      },
      complete: results => {
        if (results.errors.length > 0) {
          // CSV parsing warnings - continuing with parsed data
        }

        const data = results.data
          .filter(row => {
            // Filter out empty rows - check multiple possible ID/name columns
            return (
              (row.facility_id || row.id) &&
              (row.facility_name ||
                row.name ||
                row.institution_name ||
                row.school_name)
            )
          })
          .map(row => normalizeFacilityData(row))

        setCachedData(cacheKey, data)
        resolve(data)
      },
      error: error => {
        reject(new Error(`CSV parsing failed: ${error.message}`))
      },
    })
  })
}

// Public API functions
export const loadConfig = () => {
  return loadJson('/data/config.json', 'config')
}

export const loadLocations = () => {
  return loadJson('/data/locations.json', 'locations')
}

export const loadAttributes = category => {
  return loadJson(`/data/attributes/${category}.json`, `attributes_${category}`)
}

export const loadFacilities = category => {
  return loadCsv(
    `/data/facilities/${category}_facilities.csv`,
    `facilities_${category}`
  )
}

// Helper function to clear all cached data
export const clearCache = () => {
  const keys = Object.keys(localStorage).filter(
    key =>
      key.startsWith('config') ||
      key.startsWith('locations') ||
      key.startsWith('attributes_') ||
      key.startsWith('facilities_')
  )

  keys.forEach(key => localStorage.removeItem(key))
}

// Helper function to get cache status
export const getCacheStatus = () => {
  const status = {}

  const checkCache = key => {
    const cached = getCachedData(key)
    status[key] = {
      cached: !!cached,
      size: cached ? JSON.stringify(cached).length : 0,
      timestamp: cached ? Date.now() : null,
    }
  }

  checkCache('config')
  checkCache('locations')
  checkCache('attributes_health')
  checkCache('facilities_health')
  checkCache('attributes_education')
  checkCache('facilities_education')

  return status
}
